import React, { useState, useEffect } from "react";
import axios from "axios";
import { AsYouType, isValidPhoneNumber } from "libphonenumber-js";
import { Button, Card, Container, Form, Modal, Spinner } from "react-bootstrap";
import styles from "./index.module.css";

const ApplyNowForm = () => {
  // Initial data
  const initialData = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  };

  const queryParams = new URLSearchParams(window.location.search);
  const hashKey = queryParams.get("key");
  const employeeID = queryParams.get("empid");

  const [data, setData] = useState(initialData);

  const [isFirstNameValid, setFirstNameValid] = useState(true);
  const [isLastNameValid, setLastNameValid] = useState(true);
  const [isEmailValid, setEmailValid] = useState(true);
  const [isPhoneValid, setPhoneValid] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [whichConsentChecked, setConsentChecked] = useState({
    consentValue: "Yes",
  });

  const { consentValue } = whichConsentChecked;

  useEffect(() => {
    if (employeeID) {
      setData((prev) => ({
        ...prev,
        empID: employeeID,
      }));
    }

    if (hashKey) {
      axios
        .get(process.env.REACT_APP_SUNSOFT_API_GET_SESSION_KEY + hashKey, {
          headers: { "Content-Type": "application/json" },
        })
        .then((response) => {
          const userData = response.data.parameters;

          setData({
            firstName: userData.firstName,
            lastName: userData.lastName,
            email: userData.email,
            phone: toPhoneFormat(userData.phoneNumber),
          });

          return response;
        });
    }
  }, [hashKey, employeeID]);

  const handleConsentChange = (e) => {
    e.persist();
    setConsentChecked((prevState) => ({
      ...prevState,
      consentValue: e.target.value,
    }));
  };

  const checkNameValid = (fn, ln) => {
    if (fn.length > 1 || ln.length > 1) {
      setFirstNameValid(true);
      setLastNameValid(true);
      return true;
    }
    setFirstNameValid(false);
    setLastNameValid(false);
    return false;
  };

  const checkEmailValid = (em) => {
    if (validEmail(em)) {
      setEmailValid(true);
      return true;
    }
    setEmailValid(false);
    return false;
  };

  const checkPhoneValid = (ph) => {
    if (isValidPhoneNumber(ph, "US")) {
      setPhoneValid(true);
      return true;
    }
    setPhoneValid(false);
    return false;
  };

  const toPhoneFormat = (e) => {
    // Limit amount of numbers entered
    let phone = e;
    phone = phone.replace(/\D/g, "");
    if (phone.length > 10) {
      phone = phone.substring(0, 12);
    }

    // Change to formated form
    let ret = new AsYouType("US").input(phone);
    // Allows () to be deleted
    const retNums = ret.replace(/\D/g, "");
    if (retNums.length < 5) {
      ret = ret.replace(/\D/g, "");
    }
    return ret;
  };

  const validEmail = (value) =>
    value.length > 0 &&
    /^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(value);

  const handleSubmit = (e) => {
    e.preventDefault();
    submit();
  };

  const checkFieldValidity = () => {
    const all = [
      checkEmailValid(data.email),
      checkPhoneValid(data.phone),
      checkNameValid(data.firstName, data.lastName),
    ];

    return all.every(Boolean);
  };

  const submit = async () => {
    if (checkFieldValidity()) {
      setFormLoading(true);
      console.debug("Form filled.");
      console.debug(process.env);
      console.debug(data);

      // Insert axios post request here.
      axios
        .post(
          process.env.REACT_APP_API_TACA_CONFERENCE_MAIL,
          {
            first: data.firstName,
            last: data.lastName,
            email: data.email,
            phone: data.phone,
            consent: consentValue,
            empID: employeeID,
          },
          {
            headers: { "Content-Type": "application/json" },
          }
        )
        .then(function () {
          // Post Sucess. Cleanup.
          setData(initialData);
          setFormLoading(false);
          setShowSuccessModal(true);
        })
        .catch(function (error) {
          console.error(error);
          // Post error. Cleanup
          setData(initialData);
          setFormLoading(false);
          setShowErrorModal(true);
        });
    } else {
      console.debug("Form not fully filled.");
    }
  };

  function SuccessModal(props) {
    return (
      <Modal
        {...props}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton />
        <Modal.Body>
          <h5>Thank you for submitting! We will get in touch with you soon.</h5>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowSuccessModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  function ErrorModal(props) {
    return (
      <Modal
        {...props}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton />
        <Modal.Body>
          <h5>Something went wrong when submitting. Please try again.</h5>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowErrorModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Container className={`${styles.section}`}>
      <Card className={`${styles.card}`}>
        <Card.Title className={`${styles.card_title}`}>
          <p>REGISTER NOW</p>
        </Card.Title>
        <Card.Body className={styles.card_body}>
          <Form className={styles.card_form}>
            <Form.Group className={styles.form_group}>
              <Form.Control
                id="firstName"
                type="text"
                placeholder="First Name"
                value={data.firstName}
                className={styles.form_control}
                onChange={(e) => {
                  const { value } = e.target;
                  setData((prev) => ({
                    ...prev,
                    firstName: value,
                  }));
                  checkNameValid(value, data.lastName);
                }}
                isInvalid={!isFirstNameValid}
              />
              <Form.Control.Feedback type="invalid">
                {/* Please provide a full name. */}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className={styles.form_group}>
              <Form.Control
                id="lastName"
                type="text"
                placeholder="Last Name"
                value={data.lastName}
                className={styles.form_control}
                onChange={(e) => {
                  const { value } = e.target;
                  setData((prev) => ({
                    ...prev,
                    lastName: value,
                  }));
                  checkNameValid(data.firstName, value);
                }}
                isInvalid={!isLastNameValid}
              />
              <Form.Control.Feedback type="invalid">
                {/* Please provide a full name. */}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className={styles.form_group}>
              <Form.Control
                id="email"
                type="text"
                placeholder="Email ID"
                value={data.email}
                className={styles.form_control}
                isInvalid={!isEmailValid}
                onChange={(e) => {
                  const { value } = e.target;
                  setData((prev) => ({
                    ...prev,
                    email: value,
                  }));
                  checkEmailValid(value);
                }}
              />
              <Form.Control.Feedback type="invalid">
                {/* Please provide an email. */}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className={styles.form_group}>
              <Form.Control
                id="phone"
                type="text"
                placeholder="Phone Number"
                value={data.phone}
                className={styles.form_control}
                isInvalid={!isPhoneValid}
                onChange={(e) => {
                  const { value } = e.target;
                  setData((prev) => ({
                    ...prev,
                    phone: toPhoneFormat(value),
                  }));
                  checkPhoneValid(value);
                }}
              />
              <Form.Control.Feedback type="invalid">
                {/* Please provide a phone number. */}
              </Form.Control.Feedback>
            </Form.Group>

            <div className={styles.consent_wrapper}>
              <div className={styles.consent_title}>
                Phone Solicitation Consent
              </div>
              <div className={styles.consent_para}>
                By clicking I CONSENT below, you expressly consent to Sun West
                Mortgage Company, Inc. making (or allowing to be made on its
                behalf) a phone call, text message, or voicemail transmission to
                the phone number you entered above for the purpose of soliciting
                your purchase of consumer goods or services (including using an
                automated system for the selection or dialing of phone numbers,
                the playing of a recorded message when a connection is made, or
                the transmission of a pre-recorded voicemail), even if that
                phone number is a on a national, state, or internal Do Not Call
                Registry. You are not required to click on I CONSENT below
                (directly or indirectly) or agree to receive such phone calls,
                text messages, or voicemail transmissions as a condition of
                obtaining any goods or services from Sun West Mortgage Company,
                Inc. or its affiliates.
              </div>
              <Form.Group className={styles.consent_check_wrapper}>
                <Form.Check
                  inline
                  className={styles.consent_checkbox}
                  type="checkbox"
                  label="I consent"
                  value="Yes"
                  onChange={handleConsentChange}
                  checked={consentValue === "Yes"}
                />
                <Form.Check
                  inline
                  className={styles.consent_checkbox}
                  type="checkbox"
                  label="I do not consent"
                  value="No"
                  onChange={handleConsentChange}
                  checked={consentValue === "No"}
                />
              </Form.Group>
              <div className={styles.consent_para}>
                By pressing Get Started, you also agree to Sun West Mortgage
                Company, Inc.'s{" "}
                <a
                  href="https://www.swmc.com/terms-and-conditions"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms and Conditions
                </a>{" "}
                and{" "}
                <a
                  href="https://www.swmc.com/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                >
                  Security and Privacy Policy
                </a>
                , which you may access by clicking on those terms. 
              </div>
            </div>
            <Button
              variant="outline-dark"
              className={`${styles.apply_button}`}
              type="submit"
              onClick={handleSubmit}
            >
              {formLoading ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                "Get Started"
              )}
            </Button>
          </Form>
        </Card.Body>
      </Card>
      {showSuccessModal && (
        <SuccessModal
          show={showSuccessModal}
          onHide={() => setShowSuccessModal(false)}
        />
      )}
      {showErrorModal && (
        <ErrorModal
          show={showErrorModal}
          onHide={() => setShowErrorModal(false)}
        />
      )}
    </Container>
  );
};

export default ApplyNowForm;
